import { useState } from "react";
import { ShieldCheck, ShieldAlert } from "lucide-react";
import { CRIT_KEYS, CRIT_LABELS } from "./roadmapHelpers";

/** Reusable Programme Roadmap renderer. Honours block visibility:
 *  patient-preview hides clinician_only blocks; the clinician toggle reveals them.
 *  This same presentation is intended to back the future Patient Roadmap view. */
export const TemplatePreview = ({ data, onClose }) => {
  const [mode, setMode] = useState("patient"); // patient | clinician
  const lib = data.exercise_library || {};
  const name = (id) => (id ? lib[id]?.name || id : null);
  const showBlock = (b) => mode === "clinician" || b.visibility !== "clinician_only";

  const renderBlock = (b, i) => {
    const d = b.data || {};
    if (b.type === "exercise") {
      return (
        <li key={b.id || i} className="text-zinc-300">
          <b>{b.title || name(d.exercise_id) || "Exercise"}</b>
          {b.visibility === "clinician_only" && <span className="text-amber-400 text-[10px] ml-1">clinician only</span>}
          <span className="text-zinc-500"> · {d.sets ?? "—"} × {d.reps ?? "—"}{d.rest_seconds ? ` · rest ${d.rest_seconds}s` : ""}{d.frequency_per_week ? ` · ${d.frequency_per_week}×/wk` : ""}{d.load_guidance ? ` · ${d.load_guidance}` : ""}</span>
          {(d.easier_alternative_id || d.harder_alternative_id || d.home_alternative_id) && (
            <div className="text-zinc-500 text-[11px]">Easier: {name(d.easier_alternative_id) || "—"} · Harder: {name(d.harder_alternative_id) || "—"} · Home: {name(d.home_alternative_id) || "—"}</div>)}
          {d.instructions && <div className="text-zinc-500 text-[11px]">{d.instructions}</div>}
        </li>
      );
    }
    if (b.type === "education") return <li key={b.id || i} className="text-zinc-300"><b>{b.title || "Education"}:</b> {d.text}</li>;
    if (b.type === "custom_text") return <li key={b.id || i} className="text-zinc-300"><b>{b.title ? `${b.title}: ` : ""}</b>{d.text}</li>;
    if (b.type === "resource" || b.type === "evidence") {
      const bits = [d.authors, d.publication, d.year, d.doi].filter(Boolean).join(" · ");
      return <li key={b.id || i} className="text-zinc-300"><b>{b.title || (b.type === "evidence" ? "Reference" : "Resource")}</b>{bits ? <span className="text-zinc-500"> — {bits}</span> : ""}{d.url && <span className="text-zinc-500"> · {d.url}</span>}{d.description && <div className="text-zinc-500 text-[11px]">{d.description}</div>}</li>;
    }
    return null;
  };

  return (
    <div className="fl-card p-4 space-y-3 text-sm" data-testid="template-preview">
      <div className="flex items-center gap-2 flex-wrap">
        <h2 className="text-lg text-white font-semibold">{data.title}</h2>
        <span className="text-xs text-zinc-500">v{data.number} · {data.status} · preview (not a diagnosis)</span>
        <div className="ml-auto flex items-center gap-2">
          <div className="flex rounded-lg border border-zinc-700 overflow-hidden text-xs">
            <button className={`px-2 py-1 ${mode === "patient" ? "bg-zinc-800 text-white" : "text-zinc-400"}`} onClick={() => setMode("patient")} data-testid="preview-mode-patient">Patient view</button>
            <button className={`px-2 py-1 ${mode === "clinician" ? "bg-zinc-800 text-white" : "text-zinc-400"}`} onClick={() => setMode("clinician")} data-testid="preview-mode-clinician">Clinician view</button>
          </div>
          <button className="fl-btn-ghost text-xs" onClick={onClose} data-testid="close-preview-btn">Close</button>
        </div>
      </div>
      {data.pathway_warning && <div className="text-xs text-amber-400">{data.pathway_warning}</div>}
      {data.clinical_readiness && (() => {
        const cr = data.clinical_readiness;
        const ok = cr.patient_generation_enabled;
        return (
          <div data-testid="clinical-readiness"
               className={`rounded-md p-3 text-xs border ${ok ? "border-emerald-600/40 bg-emerald-600/10" : "border-amber-600/40 bg-amber-600/10"}`}>
            <div className="flex items-center gap-2 font-semibold">
              {ok
                ? <><ShieldCheck size={14} className="text-emerald-300" /><span className="text-emerald-300">Patient-facing generation ENABLED — all required clinical content is approved</span></>
                : <><ShieldAlert size={14} className="text-amber-300" /><span className="text-amber-300">Patient-facing generation DISABLED — clinical approvals outstanding</span></>}
            </div>
            {cr.blocking_reasons?.length > 0 && (
              <ul className="list-disc ml-5 mt-2 text-amber-200/90 space-y-0.5" data-testid="readiness-blocking">
                {cr.blocking_reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
            {cr.unapproved_exercises?.length > 0 && (
              <div className="mt-2" data-testid="readiness-unapproved-exercises"><b className="text-zinc-200">Unapproved exercises:</b> {cr.unapproved_exercises.map((x) => `${x.name} (${x.status})`).join(", ")}</div>
            )}
            {cr.pending_assessments?.length > 0 && (
              <div className="mt-1" data-testid="readiness-pending-assessments"><b className="text-zinc-200">Unapproved physical tests:</b> {cr.pending_assessments.map((x) => `${x.name} (${x.status})`).join(", ")}</div>
            )}
          </div>
        );
      })()}
      {data.patient_description && <p className="text-zinc-300">{data.patient_description}</p>}
      <div className="grid md:grid-cols-3 gap-3 text-xs text-zinc-400">
        <div><b className="text-zinc-200">Who it is for:</b> {data.intended_population || "—"}<ul className="list-disc ml-4">{data.eligibility?.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
        <div><b className="text-zinc-200">Not suitable if:</b><ul className="list-disc ml-4">{data.exclusions?.map((x, i) => <li key={i}>{x}</li>)}</ul></div>
        <div><b className="text-zinc-200">Equipment:</b> {data.equipment?.join(", ") || "—"}{data.equipment_alternatives && <div>Alternatives: {data.equipment_alternatives}</div>}</div>
      </div>
      <ol className="space-y-2">
        {data.phases?.map((p, i) => {
          const blocks = (p.content_blocks || []).filter(showBlock);
          return (
            <li key={p.id || i} className="rounded border border-zinc-800 p-3">
              <div className="text-zinc-100 font-medium">{i + 1}. {p.title}</div>
              {p.description && <div className="text-xs text-zinc-400">{p.description}</div>}
              {p.goals?.length > 0 && <div className="text-xs text-zinc-400 mt-1"><b>Goals:</b> {p.goals.join("; ")}</div>}
              {blocks.length ? <ul className="mt-2 space-y-1 text-xs">{blocks.map(renderBlock)}</ul> : <div className="text-xs text-zinc-600 mt-1">No content yet.</div>}
              {CRIT_KEYS.map((k) => (p.criteria?.[k]?.length ? (
                <div key={k} className="text-xs text-zinc-400 mt-1"><b>{CRIT_LABELS[k]}:</b> {p.criteria[k].map((c) => c.label).join("; ")}</div>
              ) : null))}
            </li>
          );
        })}
      </ol>
      {data.education && <div className="text-xs text-zinc-300"><b>Education:</b> {data.education}</div>}
      {(data.references?.length > 0 || data.evidence_references?.length > 0) && (
        <div className="text-xs text-zinc-500"><b>Evidence & references:</b>
          <ul className="list-disc ml-4">
            {data.references?.filter(showBlock).map((r, i) => <li key={r.id || i}>{r.title}{[r.authors, r.publication, r.year].filter(Boolean).length ? ` — ${[r.authors, r.publication, r.year].filter(Boolean).join(", ")}` : ""}</li>)}
            {data.evidence_references?.map((x, i) => <li key={`e${i}`}>{x}</li>)}
          </ul></div>
      )}
      <div className="text-[11px] text-zinc-600">Last clinical review: {data.last_clinical_review || "—"} · Next review due: {data.next_review_due || "—"}</div>
    </div>
  );
};
